import type { RollbackResource } from '../types/rollback';
import { toFriendlyName } from './formatters';
import { getTypeLabelByApiName } from './resourceTypes';

/**
 * A rollback plan's resources for a single Terraform resource type, split by the action the
 * rollback will take. Pre-existing resources (e.g. divisions that existed before the migration)
 * are surfaced in {@link preExisting} so they are shown as "kept", never as deletions.
 */
export interface ResourceTypeGroup {
    resourceType: string;
    label: string;
    deletes: RollbackResource[];
    reverts: RollbackResource[];
    restores: RollbackResource[];
    needsReview: RollbackResource[];
    preExisting: RollbackResource[];
}

/**
 * Groups rollback resources by their Terraform resource type and rollback action.
 *
 * Unknown/indeterminate actions are routed to {@link ResourceTypeGroup.needsReview} — never treated
 * as a destructive default — so the UI can never silently present an ambiguous resource as a deletion
 * or restore.
 */
export function groupRollbackResourcesByType(resources: RollbackResource[]): ResourceTypeGroup[] {
    const map = new Map<string, ResourceTypeGroup>();
    for (const r of resources) {
        if (!map.has(r.resourceType)) {
            map.set(r.resourceType, {
                resourceType: r.resourceType,
                label: getTypeLabelByApiName(toFriendlyName(r.resourceType)),
                deletes: [],
                reverts: [],
                restores: [],
                needsReview: [],
                preExisting: []
            });
        }
        const g = map.get(r.resourceType)!;
        if (r.action === 'delete') g.deletes.push(r);
        else if (r.action === 'revert') g.reverts.push(r);
        else if (r.action === 'restore') g.restores.push(r);
        else if (r.action === 'none') g.preExisting.push(r);
        else g.needsReview.push(r);
    }
    return Array.from(map.values());
}
